/**
 * Money conversions at the API boundary (SPEC §2). The API speaks integer
 * kopeks; the UI speaks rubles. Everything here is STRING/INTEGER math —
 * floats never touch an amount ("0.1 + 0.2" has no business near real money).
 */

/** Thousand-group separator used in display strings (regular space). */
const GROUP_SEP = ' ';
/** Currency suffix. */
const RUB = '₽';

/** "1234567" → "1 234 567" (pure string math, right-to-left groups of 3). */
function groupThousands(digits: string): string {
  let out = '';
  for (let i = 0; i < digits.length; i++) {
    const fromEnd = digits.length - i;
    out += digits[i];
    if (fromEnd > 1 && fromEnd % 3 === 1) out += GROUP_SEP;
  }
  return out;
}

/**
 * Rubles (user input) → integer kopeks. Accepts "150000.55", "150 000,55",
 * a leading "-", comma or dot as the decimal separator, regular/nbsp/narrow
 * spaces as group separators. Throws on anything else (callers validate
 * first via {@link isValidRubInput}).
 */
export function rubToKopeks(input: string | number): number {
  // Numbers are stringified first so the arithmetic below stays integer-only.
  const raw = typeof input === 'number' ? String(input) : input;
  const normalized = raw.replace(/\s/g, '').replace(',', '.');
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(normalized);
  if (!match) throw new Error(`Invalid ruble amount: "${raw}"`);
  const [, sign, whole, frac = ''] = match;
  const kopeks = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
  if (!Number.isSafeInteger(kopeks)) throw new Error(`Amount out of range: "${raw}"`);
  return sign === '-' ? -kopeks : kopeks;
}

/** True when {@link rubToKopeks} would accept the string. */
export function isValidRubInput(input: string): boolean {
  const normalized = input.replace(/\s/g, '').replace(',', '.');
  return /^(-?)(\d+)(?:\.(\d{1,2}))?$/.test(normalized);
}

/**
 * Integer kopeks → plain ruble string for form fields: 15000055 → "150000.55",
 * 15000000 → "150000" (kopeks shown only when non-zero, always two digits).
 */
export function kopeksToRubString(kopeks: number): string {
  if (!Number.isSafeInteger(kopeks)) throw new Error(`Invalid kopeks: ${kopeks}`);
  const sign = kopeks < 0 ? '-' : '';
  const abs = Math.abs(kopeks);
  const rub = Math.trunc(abs / 100);
  const kop = abs % 100;
  return kop === 0 ? `${sign}${rub}` : `${sign}${rub}.${String(kop).padStart(2, '0')}`;
}

/**
 * Integer kopeks → display string: 15000000 → "150 000 ₽",
 * 15000055 → "150 000,55 ₽" (Russian decimal comma, space thousand groups).
 */
export function formatKopeks(kopeks: number): string {
  if (!Number.isSafeInteger(kopeks)) throw new Error(`Invalid kopeks: ${kopeks}`);
  const sign = kopeks < 0 ? '-' : '';
  const abs = Math.abs(kopeks);
  const rub = groupThousands(String(Math.trunc(abs / 100)));
  const kop = abs % 100;
  const kopPart = kop === 0 ? '' : `,${String(kop).padStart(2, '0')}`;
  return `${sign}${rub}${kopPart} ${RUB}`;
}

/**
 * Live-typing formatter for amount inputs: keeps digits and one decimal comma
 * (max 2 decimals), regroups thousands on every keystroke.
 * "1500000" → "1 500 000"; "150000,5" → "150 000,5"; "12." → "12,".
 */
export function formatRubTyping(raw: string, allowNegative = false): string {
  const negative = allowNegative && raw.trimStart().startsWith('-');
  const normalized = raw.replace(',', '.');
  const dot = normalized.indexOf('.');
  const wholeDigits = (dot === -1 ? normalized : normalized.slice(0, dot)).replace(/\D/g, '');
  const fracDigits = dot === -1 ? null : normalized.slice(dot + 1).replace(/\D/g, '').slice(0, 2);
  if (wholeDigits === '' && fracDigits === null) return negative ? '-' : '';
  const whole = groupThousands(wholeDigits.replace(/^0+(?=\d)/, '') || '0');
  const sign = negative ? '-' : '';
  return fracDigits === null ? `${sign}${whole}` : `${sign}${whole},${fracDigits}`;
}
